package com.evoapps.evoshift.ui.finance

import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.domain.model.LedgerKind
import com.evoapps.evoshift.widgets.EvoSegmented
import evoshift.composeapp.generated.resources.ledger_kind_cost
import evoshift.composeapp.generated.resources.ledger_kind_label
import evoshift.composeapp.generated.resources.ledger_kind_revenue
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.automirrored.filled.ReceiptLong
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.FixedCost
import com.evoapps.evoshift.domain.usecase.finance.OneOffCostInput
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.dayShort
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoConfirmDialog
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.RegularCard
import com.evoapps.evoshift.widgets.formatEditable
import com.evoapps.evoshift.widgets.parseDecimal
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cost_kind_one_off
import evoshift.composeapp.generated.resources.cost_kind_rule
import evoshift.composeapp.generated.resources.cost_kind_shared
import evoshift.composeapp.generated.resources.shared_cost_origin
import evoshift.composeapp.generated.resources.shared_cost_origin_hint
import evoshift.composeapp.generated.resources.delete
import evoshift.composeapp.generated.resources.edit
import evoshift.composeapp.generated.resources.error_one_off_amount
import evoshift.composeapp.generated.resources.field_required
import evoshift.composeapp.generated.resources.financials_fixed_costs
import evoshift.composeapp.generated.resources.name
import evoshift.composeapp.generated.resources.number_invalid
import evoshift.composeapp.generated.resources.one_off_cost_add
import evoshift.composeapp.generated.resources.one_off_cost_amount
import evoshift.composeapp.generated.resources.one_off_cost_day
import evoshift.composeapp.generated.resources.one_off_cost_delete_confirm
import evoshift.composeapp.generated.resources.one_off_cost_edit_title
import evoshift.composeapp.generated.resources.one_off_cost_name_hint
import evoshift.composeapp.generated.resources.one_off_cost_none
import evoshift.composeapp.generated.resources.one_off_cost_note
import evoshift.composeapp.generated.resources.one_off_cost_days_range
import evoshift.composeapp.generated.resources.one_off_cost_from
import evoshift.composeapp.generated.resources.one_off_cost_period
import evoshift.composeapp.generated.resources.one_off_cost_period_day
import evoshift.composeapp.generated.resources.one_off_cost_period_month
import evoshift.composeapp.generated.resources.one_off_cost_period_range
import evoshift.composeapp.generated.resources.one_off_cost_spread_hint
import evoshift.composeapp.generated.resources.one_off_cost_to
import com.evoapps.evoshift.ui.util.monthInDateRes
import com.evoapps.evoshift.widgets.LocalOnColours
import kotlinx.datetime.number
import evoshift.composeapp.generated.resources.save
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import org.jetbrains.compose.resources.stringResource

/*
 * SH-84: the month's costs on its money screen (Financials): what the recurring rules generated
 * ("Fixed") and the one-off expenses ("One-off"), which money editors add, change and delete.
 * Fixed costs are the month's snapshot of the rules and are changed in the rules.
 */

/** SH-87: revenue in green with a "+", costs as they were. */
internal val RevenueGreen = Color(0xFF2E7D32)

/** SH-87: an amount as the lists show it: "+1 200,00 zł" for revenue, "1 200,00 zł" for a cost. */
internal fun ledgerAmount(amount: Double, kind: LedgerKind, currency: String): String =
    if (kind == LedgerKind.REVENUE) "+" + formatMoney(amount, currency) else formatMoney(amount, currency)

@Composable
fun ledgerKindLabel(kind: LedgerKind): String =
    stringResource(if (kind == LedgerKind.REVENUE) Res.string.ledger_kind_revenue else Res.string.ledger_kind_cost)

/** SH-87: "Type": Cost / Revenue, in a dialog. */
@Composable
fun LedgerKindToggle(kind: LedgerKind, onChange: (LedgerKind) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(stringResource(Res.string.ledger_kind_label), style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha = 0.8f))
        EvoSegmented(
            options = LedgerKind.entries.toList(),
            selected = kind,
            onSelect = onChange,
            optionLabel = { ledgerKindLabel(it) }
        )
    }
}

/** "2026-10-13 (Tue)": a day of the month as the expense dialog and the list show it. */
@Composable
internal fun costDayLabel(date: LocalDate): String = "$date (${dayShort(date.dayOfWeek)})"

/** How long a one-off item lasts: one day, some days of the month, or all of it. */
enum class CostPeriod { DAY, RANGE, MONTH }

/**
 * The days a cost covers as the month's list shows them: its date for a single day, "1–15
 * października" for some days, "Cały miesiąc" for every day of its month.
 */
@Composable
internal fun costPeriodLabel(cost: FixedCost): String {
    if (!cost.isPeriod) return cost.date
    val first = runCatching { LocalDate.parse(cost.date) }.getOrNull() ?: return cost.date
    val last = runCatching { LocalDate.parse(cost.lastDate) }.getOrNull() ?: return cost.date
    val month = monthDays(first.year, first.month.number)
    if (first == month.first() && last == month.last()) return stringResource(Res.string.one_off_cost_period_month)
    return stringResource(Res.string.one_off_cost_days_range, first.day, last.day, stringResource(monthInDateRes(first.month.number)))
}

/** The days of [month] of [year], in order. */
internal fun monthDays(year: Int, month: Int): List<LocalDate> {
    val first = LocalDate(year, month, 1)
    return generateSequence(first) { it.plus(DatePeriod(days = 1)) }.takeWhile { it.month == first.month }.toList()
}

/**
 * The month's costs: a card per cost (name, day, note, the kind, the amount) and, with [canEdit]
 * (Gate.MONEY_EDITING), "Add one-off expense" and each one-off's edit and delete buttons.
 */
@Composable
fun MonthCostsSection(
    costs: List<FixedCost>,
    currency: String,
    canEdit: Boolean,
    onAdd: () -> Unit,
    onEdit: (FixedCost) -> Unit,
    onDelete: (FixedCost) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            stringResource(Res.string.financials_fixed_costs),
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = DeepNavy
        )
        if (costs.isEmpty()) {
            Text(stringResource(Res.string.one_off_cost_none), style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.7f))
        }
        costs.forEach { cost ->
            RegularCard {
                Row(
                    modifier = Modifier.padding(start = 12.dp, top = 8.dp, bottom = 8.dp, end = 4.dp).fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(Modifier.weight(1f)) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text(cost.name, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f, fill = false))
                            Spacer(Modifier.width(8.dp))
                            CostKindTag(cost)
                            if (cost.isRevenue) {
                                Spacer(Modifier.width(4.dp))
                                RevenueTag()
                            }
                        }
                        Text(costPeriodLabel(cost), style = MaterialTheme.typography.bodySmall)
                        cost.note?.let { Text(it, style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.7f)) }
                        // SH-131: a part of the owner's shared cost: read-only here, with its origin.
                        cost.allocation?.takeIf { cost.isAllocated }?.let { allocation ->
                            Text(
                                stringResource(Res.string.shared_cost_origin, cost.name, formatPercent(allocation.sharePercent)),
                                style = MaterialTheme.typography.bodySmall,
                                color = DeepNavy.copy(alpha = 0.8f)
                            )
                            Text(stringResource(Res.string.shared_cost_origin_hint), style = MaterialTheme.typography.labelSmall, color = DeepNavy.copy(alpha = 0.6f))
                        }
                    }
                    Text(
                        ledgerAmount(cost.amount, cost.kind, cost.currency.ifBlank { currency }),
                        fontWeight = FontWeight.Bold,
                        color = if (cost.isRevenue) RevenueGreen else DeepNavy
                    )
                    if (canEdit && cost.isOneOff) {
                        IconButtonWithTooltip(onClick = { onEdit(cost) }, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.edit)) {
                            Icon(Icons.Default.Edit, contentDescription = stringResource(Res.string.edit), modifier = Modifier.size(20.dp))
                        }
                        IconButtonWithTooltip(onClick = { onDelete(cost) }, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.delete)) {
                            Icon(
                                Icons.Default.Delete,
                                contentDescription = stringResource(Res.string.delete),
                                modifier = Modifier.size(20.dp),
                                tint = MaterialTheme.colorScheme.error
                            )
                        }
                    } else if (canEdit) {
                        // Keeps the amounts of both kinds in one column.
                        Spacer(Modifier.width(96.dp))
                    }
                }
            }
        }
        if (canEdit) {
            OutlinedButton(onClick = onAdd, modifier = Modifier.fillMaxWidth().handCursor(), shape = RoundedCornerShape(12.dp)) {
                Icon(Icons.Default.Add, contentDescription = null, tint = DeepNavy)
                Spacer(Modifier.width(8.dp))
                Text(stringResource(Res.string.one_off_cost_add), color = DeepNavy)
            }
        }
    }
}

/** SH-87: "Revenue", as a small green tag next to an item's name. */
@Composable
private fun RevenueTag() {
    Text(
        stringResource(Res.string.ledger_kind_revenue),
        style = MaterialTheme.typography.labelSmall,
        color = Color.White,
        modifier = Modifier.clip(RoundedCornerShape(6.dp)).background(RevenueGreen).padding(horizontal = 6.dp, vertical = 2.dp)
    )
}

/** "Fixed" (from a recurring rule), "One-off" or (SH-131) "Shared", as a small tag next to the cost's name. */
@Composable
private fun CostKindTag(cost: FixedCost) {
    val oneOff = cost.isOneOff
    val color = when {
        oneOff -> DarkGold
        cost.isAllocated -> DeepNavy
        else -> DeepNavy.copy(alpha = 0.6f)
    }
    Text(
        stringResource(
            when {
                oneOff -> Res.string.cost_kind_one_off
                cost.isAllocated -> Res.string.cost_kind_shared
                else -> Res.string.cost_kind_rule
            }
        ),
        style = MaterialTheme.typography.labelSmall,
        color = if (oneOff) DeepNavy else Color.White,
        modifier = Modifier.clip(RoundedCornerShape(6.dp)).background(color).padding(horizontal = 6.dp, vertical = 2.dp)
    )
}

/**
 * Adding (no [cost]) or changing a one-off expense of the month [year]/[month]: name, net amount
 * in [currency], its period (a day of the month, the first or [defaultDay] when it is in the
 * month; some days of it; or the whole month), a note. A period's amount counts once in the
 * month and is spread evenly over its days.
 */
@Composable
fun OneOffCostDialog(
    year: Int,
    month: Int,
    currency: String,
    cost: FixedCost?,
    defaultDay: LocalDate?,
    onDismiss: () -> Unit,
    onSave: (OneOffCostInput) -> Unit
) {
    val days = remember(year, month) { monthDays(year, month) }
    val first = days.first()
    val last = days.last()
    val costStart = cost?.date?.let { runCatching { LocalDate.parse(it) }.getOrNull() }?.takeIf { it in days }
    val costEnd = cost?.lastDate?.let { runCatching { LocalDate.parse(it) }.getOrNull() }?.takeIf { it in days && costStart != null && it > costStart }
    var name by remember { mutableStateOf(cost?.name.orEmpty()) }
    var amountText by remember { mutableStateOf(cost?.amount?.let { formatEditable(it) }.orEmpty()) }
    var day by remember { mutableStateOf(costStart ?: defaultDay?.takeIf { it in days } ?: first) }
    var period by remember {
        mutableStateOf(
            when {
                costEnd == null -> CostPeriod.DAY
                costStart == first && costEnd == last -> CostPeriod.MONTH
                else -> CostPeriod.RANGE
            }
        )
    }
    // A range is at least two days: it starts before the month's last day and ends after its start.
    var rangeStart by remember { mutableStateOf(costStart?.takeIf { costEnd != null } ?: first) }
    var rangeEnd by remember { mutableStateOf(costEnd ?: last) }
    var note by remember { mutableStateOf(cost?.note.orEmpty()) }
    var kind by remember { mutableStateOf(cost?.kind ?: LedgerKind.COST) }
    var tried by remember { mutableStateOf(false) }

    val amount = parseDecimal(amountText)
    val required = stringResource(Res.string.field_required)
    val amountError = when {
        amountText.isNotBlank() && amount == null -> stringResource(Res.string.number_invalid)
        amount != null && amount <= 0.0 -> stringResource(Res.string.error_one_off_amount)
        tried && amountText.isBlank() -> required
        else -> null
    }
    val valid = name.isNotBlank() && amount != null && amount > 0.0
    val (start, end) = when (period) {
        CostPeriod.DAY -> day to null
        CostPeriod.RANGE -> rangeStart to rangeEnd
        CostPeriod.MONTH -> first to last
    }

    EvoDialog(
        title = stringResource(if (cost == null) Res.string.one_off_cost_add else Res.string.one_off_cost_edit_title),
        icon = Icons.AutoMirrored.Filled.ReceiptLong,
        onDismiss = onDismiss,
        confirm = DialogAction(
            stringResource(Res.string.save),
            {
                tried = true
                if (valid) {
                    onSave(
                        OneOffCostInput(
                            name = name.trim(),
                            amount = amount,
                            date = start.toString(),
                            currency = currency,
                            note = note,
                            kind = kind,
                            endDate = end?.toString()
                        )
                    )
                }
            }
        )
    ) {
        LedgerKindToggle(kind) { kind = it }
        EvoTextField(
            value = name,
            onValueChange = { name = it },
            label = stringResource(Res.string.name),
            placeholder = stringResource(Res.string.one_off_cost_name_hint),
            error = required.takeIf { tried && name.isBlank() }
        )
        EvoTextField(
            value = amountText,
            onValueChange = { amountText = it },
            label = stringResource(Res.string.one_off_cost_amount, currency),
            keyboardType = KeyboardType.Decimal,
            suffix = currency,
            error = amountError
        )
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(stringResource(Res.string.one_off_cost_period), style = MaterialTheme.typography.labelMedium, color = LocalOnColours.current.onSurfaceVariant)
            EvoSegmented(
                options = CostPeriod.entries.toList(),
                selected = period,
                onSelect = { chosen ->
                    // From one day to some days: the range starts on that day (and runs to the month's end).
                    if (chosen == CostPeriod.RANGE && period == CostPeriod.DAY) {
                        rangeStart = if (day < last) day else days[days.size - 2]
                        if (rangeEnd <= rangeStart) rangeEnd = last
                    }
                    if (chosen == CostPeriod.DAY && period == CostPeriod.RANGE) day = rangeStart
                    period = chosen
                },
                optionLabel = {
                    stringResource(
                        when (it) {
                            CostPeriod.DAY -> Res.string.one_off_cost_period_day
                            CostPeriod.RANGE -> Res.string.one_off_cost_period_range
                            CostPeriod.MONTH -> Res.string.one_off_cost_period_month
                        }
                    )
                }
            )
        }
        when (period) {
            CostPeriod.DAY -> EvoDropdown(
                label = stringResource(Res.string.one_off_cost_day),
                options = days,
                selected = day,
                onSelected = { day = it },
                optionLabel = { costDayLabel(it) }
            )
            CostPeriod.RANGE -> {
                EvoDropdown(
                    label = stringResource(Res.string.one_off_cost_from),
                    options = days.dropLast(1),
                    selected = rangeStart,
                    onSelected = {
                        rangeStart = it
                        if (rangeEnd <= it) rangeEnd = it.plus(DatePeriod(days = 1))
                    },
                    optionLabel = { costDayLabel(it) }
                )
                EvoDropdown(
                    label = stringResource(Res.string.one_off_cost_to),
                    options = days.filter { it > rangeStart },
                    selected = rangeEnd,
                    onSelected = { rangeEnd = it },
                    optionLabel = { costDayLabel(it) }
                )
            }
            CostPeriod.MONTH -> Unit
        }
        if (end != null) {
            val count = days.count { it in start..end }
            val perDay = amount?.takeIf { it > 0.0 }?.let { formatMoney(it / count, currency) }
            Text(
                if (perDay != null) stringResource(Res.string.one_off_cost_spread_hint, count, perDay)
                else stringResource(Res.string.one_off_cost_days_range, start.day, end.day, stringResource(monthInDateRes(month))),
                style = MaterialTheme.typography.bodySmall,
                color = LocalOnColours.current.onSurfaceVariant
            )
        }
        EvoTextField(
            value = note,
            onValueChange = { note = it },
            label = stringResource(Res.string.one_off_cost_note),
            singleLine = false,
            minLines = 2,
            imeAction = ImeAction.Default
        )
    }
}

/** Removing a one-off expense from the month. */
@Composable
fun DeleteOneOffCostDialog(cost: FixedCost, onConfirm: () -> Unit, onDismiss: () -> Unit) {
    EvoConfirmDialog(
        title = stringResource(Res.string.delete),
        text = stringResource(Res.string.one_off_cost_delete_confirm, cost.name),
        confirmLabel = stringResource(Res.string.delete),
        onConfirm = onConfirm,
        onDismiss = onDismiss,
        destructive = true,
        icon = Icons.Default.Delete
    )
}
